import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarRange, Search, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStartStr = () => todayStr().slice(0, 8) + '01';

export const MonthlyInvoiceDialog = ({ open, onOpenChange, deliveryOrders, onCreated }) => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(monthStartStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [query, setQuery] = useState('');
  const [customer, setCustomer] = useState('');
  const [creating, setCreating] = useState(false);
  const [custMap, setCustMap] = useState({ byEmail: {}, byName: {}, contactsByCompany: {} });

  React.useEffect(() => {
    if (!open) return;
    axios.get(`${BACKEND_URL}/api/customers/`).then((r) => {
      const byEmail = {}, byName = {}, contactsByCompany = {};
      (r.data || []).forEach((c) => {
        const comp = (c.company_name || '').trim();
        if (!comp) return;
        [c.contact_email, c.company_email].forEach((e) => {
          if (e && e.includes('@')) byEmail[e.trim().toLowerCase()] = comp;
        });
        if (c.contact_person) {
          byName[c.contact_person.trim().toLowerCase()] = comp;
          contactsByCompany[comp.toLowerCase()] = [
            ...(contactsByCompany[comp.toLowerCase()] || []), c.contact_person.trim()];
        }
      });
      setCustMap({ byEmail, byName, contactsByCompany });
    }).catch(() => {});
  }, [open]);

  const customers = useMemo(() => {
    const map = {};
    (deliveryOrders || []).forEach((d) => {
      const date = d.delivery_date || '';
      if (date >= startDate && date <= endDate && d.customer_name) {
        if (!map[d.customer_name]) {
          const company = custMap.byEmail[(d.customer_email || '').trim().toLowerCase()]
            || custMap.byName[d.customer_name.trim().toLowerCase()] || '';
          const contacts = [
            ...(custMap.contactsByCompany[(company || d.customer_name).trim().toLowerCase()] || []),
          ];
          map[d.customer_name] = { total: 0, unbilled: 0, company, contacts };
        }
        if (d.pic_name && !map[d.customer_name].contacts.includes(d.pic_name)) {
          map[d.customer_name].contacts.push(d.pic_name);
        }
        map[d.customer_name].total += 1;
        if (!d.invoice_id && !d.billed) map[d.customer_name].unbilled += 1;
      }
    });
    return Object.entries(map).sort((a, b) =>
      (a[1].company || a[0]).localeCompare(b[1].company || b[0]));
  }, [deliveryOrders, startDate, endDate, custMap]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(([name, c]) =>
      name.toLowerCase().includes(q) || (c.company || '').toLowerCase().includes(q)
      || (c.contacts || []).some((p) => p.toLowerCase().includes(q)));
  }, [customers, query]);

  const handleCreate = async () => {
    if (!customer) { toast.error('Please select a customer'); return; }
    if (!startDate || !endDate) { toast.error('Please pick the billing period'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/invoices/monthly`, {
        customer_name: customer,
        start_date: startDate,
        end_date: endDate,
      });
      toast.success(`Draft ${res.data.document_number} created — ${res.data.do_count} DO(s), $${(res.data.total_amount || 0).toFixed(2)}. Double-check and edit before sending.`, { duration: 9000 });
      onOpenChange(false);
      onCreated?.();
      navigate(`/admin/billing/invoice/${res.data.invoice_id}/edit`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="monthly-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange size={20} className="text-green-700" /> Monthly / Period Invoice
          </DialogTitle>
          <DialogDescription>
            Combine a customer's <b>unbilled</b> delivery orders in the period below into ONE draft invoice. It opens in the edit page so you can double-check before sending — nothing is emailed automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>From</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setCustomer(''); }}
                data-testid="monthly-invoice-start-date"
              />
            </div>
            <div>
              <Label>To</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setCustomer(''); }}
                data-testid="monthly-invoice-end-date"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" className="text-xs h-7" data-testid="monthly-invoice-preset-thismonth"
              onClick={() => { setStartDate(monthStartStr()); setEndDate(todayStr()); setCustomer(''); }}>
              This month so far
            </Button>
            <Button type="button" variant="outline" size="sm" className="text-xs h-7" data-testid="monthly-invoice-preset-lastmonth"
              onClick={() => {
                const now = new Date();
                const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const last = new Date(now.getFullYear(), now.getMonth(), 0);
                const fmt = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
                setStartDate(fmt(first)); setEndDate(fmt(last)); setCustomer('');
              }}>
              Last full month
            </Button>
          </div>
          <div>
            <Label>Customer</Label>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setCustomer(''); }}
                placeholder="Search by company or contact name..."
                className="pl-9"
                data-testid="monthly-invoice-customer-search"
              />
            </div>
            <div className="mt-2 max-h-40 overflow-y-auto border rounded-md divide-y" data-testid="monthly-invoice-customer-list">
              {matches.length === 0 && (
                <p className="text-xs text-gray-500 p-3">No delivery orders found{query ? ' matching your search' : ''} in this period.</p>
              )}
              {matches.map(([name, c]) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => { setCustomer(name); setQuery(name); }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-green-50 ${customer === name ? 'bg-green-50' : 'bg-white'}`}
                  data-testid={`monthly-invoice-customer-option-${name.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <span className="flex items-center gap-2 font-medium text-gray-800">
                    {customer === name && <CheckCircle2 size={15} className="text-green-600" />}
                    <span>
                      {c.company ? (
                        <>
                          {c.company}
                          <span className="block text-[11px] font-normal text-gray-500">{name}</span>
                        </>
                      ) : name}
                    </span>
                  </span>
                  <span className={`text-xs ${c.unbilled > 0 ? 'text-green-700 font-semibold' : 'text-gray-400'}`}>
                    {c.unbilled} unbilled / {c.total} DO{c.total > 1 ? 's' : ''}
                  </span>
                </button>
              ))}
            </div>
            {customer && (
              <p className="text-xs text-green-700 mt-1" data-testid="monthly-invoice-selected-customer">
                Selected: <b>{customer}</b>
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !customer}
            className="bg-green-600 hover:bg-green-700"
            data-testid="monthly-invoice-generate-btn"
          >
            {creating ? 'Generating...' : 'Generate Draft & Review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
